import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import api from '../services/api';
import Navbar from '../components/Navbar';
import './CategoriesAndBrands.css';

const CategoriesAndBrands = () => {
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('categories');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    name_fr: '',
    description: '',
    description_ar: '',
    description_fr: '',
    is_active: true,
  });

  const { user, logout } = useAuth();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [categoriesRes, brandsRes] = await Promise.all([
        api.get('/items/categories/'),
        api.get('/items/brands/'),
      ]);

      setCategories(categoriesRes.data.results || categoriesRes.data);
      setBrands(brandsRes.data.results || brandsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      
      // Try to use cached data when offline
      if (!navigator.onLine) {
        const { getFromCache } = await import('../services/cacheService');
        const [cachedCategories, cachedBrands] = await Promise.all([
          getFromCache('CATEGORIES'),
          getFromCache('BRANDS')
        ]);
        
        if (cachedCategories) {
          setCategories(Array.isArray(cachedCategories) ? cachedCategories : cachedCategories.results || []);
        }
        if (cachedBrands) {
          setBrands(Array.isArray(cachedBrands) ? cachedBrands : cachedBrands.results || []);
        }
        
        if (cachedCategories || cachedBrands) {
          console.log('[CategoriesAndBrands] Using cached data');
          return;
        }
      }
      
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const endpoint = activeTab === 'categories' ? 'categories' : 'brands';
      
      if (editingItem) {
        await api.patch(
          `/items/${endpoint}/${editingItem.id}/`,
          formData
        );
        toast.success(t('updated'));
      } else {
        await api.post(
          `/items/${endpoint}/`,
          formData
        );
        toast.success(t('created'));
      }

      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(t('error'));
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      name_ar: item.name_ar || '',
      name_fr: item.name_fr || '',
      description: item.description || '',
      description_ar: item.description_ar || '',
      description_fr: item.description_fr || '',
      is_active: item.is_active,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('deleteConfirm'))) {
      try {
        const endpoint = activeTab === 'categories' ? 'categories' : 'brands';
        await api.delete(
          `/items/${endpoint}/${id}/`
        );
        toast.success(t('deleted'));
        fetchData();
      } catch (error) {
        console.error('Error:', error);
        const errorCode = error.response?.data?.errorCode;
        if (errorCode === 'cannot_delete_category_with_items') {
          toast.error(t('cannotDeleteCategoryWithItems'));
        } else if (errorCode === 'cannot_delete_brand_with_items') {
          toast.error(t('cannotDeleteBrandWithItems'));
        } else if (error.response?.status === 400 && error.response?.data?.error) {
          toast.error(error.response.data.error);
        } else {
          toast.error(t('error'));
        }
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      name_ar: '',
      name_fr: '',
      description: '',
      description_ar: '',
      description_fr: '',
      is_active: true,
    });
    setEditingItem(null);
  };

  const getData = () => activeTab === 'categories' ? categories : brands;

  const filteredData = getData().filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.name_ar && item.name_ar.includes(searchTerm)) ||
    (item.name_fr && item.name_fr.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getItemName = (item) => {
    if (i18n.language === 'ar') return item.name_ar || item.name;
    if (i18n.language === 'fr') return item.name_fr || item.name;
    return item.name;
  };



  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return <div className="loading">{t('loading')}</div>;
  }

  return (
    <div className="cat-brands-page">
      <Navbar />

      <div className="cat-brands-content">
        <header className="cat-brands-header">
          <div className="header-left">
            <div>
              <h1>{t('categoriesAndBrands')}</h1>
              <p>{user?.market_name}</p>
            </div>
          </div>
          <div className="header-actions"></div>
        </header>

        <div className="tabs">
          <button
            className={`tab-btn ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('categories');
              setSearchTerm('');
            }}
          >
            {t('categories')}
          </button>
          <button
            className={`tab-btn ${activeTab === 'brands' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('brands');
              setSearchTerm('');
            }}
          >
            {t('brands')}
          </button>
        </div>

        <div className="controls">
          <input
            type="text"
            placeholder={t('search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="btn btn-primary"
          >
            + {activeTab === 'categories' ? t('addCategory') : t('addBrand')}
          </button>
        </div>

        <div className="items-grid">
              {filteredData.map((item) => {
                const primaryName = getItemName(item);
                const normalizeName = (value) => (value || '')
                  .normalize('NFKC')
                  .replace(/[\u064B-\u065F]/g, '')
                  .replace(/[()\/\-\s]+/g, '')
                  .trim()
                  .toLowerCase();
                const primaryKey = normalizeName(primaryName);

                const candidates = [];
                if (i18n.language !== 'en' && item.name) candidates.push(item.name);
                if (i18n.language !== 'ar' && item.name_ar) candidates.push(item.name_ar);
                if (i18n.language !== 'fr' && item.name_fr) candidates.push(item.name_fr);

                const uniqueOtherNames = Array.from(
                  new Set(
                    candidates
                      .map((name) => name.trim())
                      .filter((name) => name && name !== primaryName)
                      .filter((name) => normalizeName(name) !== primaryKey)
                  )
                );

                return (
            <div key={item.id} className="item-card">
              <div className="item-header">
                <h3>{primaryName}</h3>
                {!item.is_active && <span className="badge-inactive">Inactive</span>}
              </div>

              <div className="item-details">
                {uniqueOtherNames.length > 0 && (
                  <div className="detail-item">
                    <span className="value alt-names">({uniqueOtherNames.join(' / ')})</span>
                  </div>
                )}
                {item.description && (
                  <div className="detail-item">
                    <span className="label">{t('description')}:</span>
                    <span className="value">{item.description}</span>
                  </div>
                )}
              </div>

              <div className="item-actions">
                <button
                  onClick={() => handleEdit(item)}
                  className="btn btn-secondary"
                >
                  {t('edit')}
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  className="btn btn-danger"
                >
                  {t('delete')}
                </button>
              </div>
            </div>
                );
              })}
        </div>

        {filteredData.length === 0 && (
          <div className="no-data">
            {t('noData')}
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {editingItem
                  ? activeTab === 'categories'
                    ? t('editCategory')
                    : t('editBrand')
                  : activeTab === 'categories'
                  ? t('addCategory')
                  : t('addBrand')}
              </h2>
              <button
                className="close-btn"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="form">
              <div className="form-group">
                <label>{t('english')} *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>العربية</label>
                <input
                  type="text"
                  name="name_ar"
                  value={formData.name_ar}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Français</label>
                <input
                  type="text"
                  name="name_fr"
                  value={formData.name_fr}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>{t('description')}</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>الوصف (العربية)</label>
                <textarea
                  name="description_ar"
                  value={formData.description_ar}
                  onChange={handleChange}
                  rows="3"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Description (Français)</label>
                <textarea
                  name="description_fr"
                  value={formData.description_fr}
                  onChange={handleChange}
                  rows="3"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                  />
                  {t('active')}
                </label>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn btn-secondary"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  {editingItem ? t('save') : t('create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoriesAndBrands;
